import React from 'react';

import { styled } from '@mui/material/styles';

import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { GET_CASH_DROP_DATA } from './gql/query';
import { useQuery } from "@apollo/client";
import CashDropChart from './components/CashDropChart';
import CashDropTable from './components/CashDropTable';

const ToggleButtonStyled = styled(ToggleButton)({
    "&.Mui-selected, &.Mui-selected:hover": {
        color: "white",
        backgroundColor: '#ff6600',
        fontWeight: 'bold'
    }
});



function CashPage({ selected_site }) {
    const [selectedSite, setSelectedSite] = React.useState('');
    const [cashDropData, setCashDropData] = React.useState({});
    const [chartMode, setChartMode] = React.useState('daily');
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);

    const [uiErrorMessage, setUiErrorMessage] = React.useState('');

    const today = new Date();
    const _30DaysAgo = new Date();
    _30DaysAgo.setDate(today.getDate() - 30)
    const [startDate, setStartDate] = React.useState( dayjs(_30DaysAgo.toISOString().split('T')[0]) );
    const [endDate, setEndDate] = React.useState( dayjs(today.toISOString().split('T')[0]) );

    const { loading: cashDropDataLoading, error, data: cashDropGqlData } = useQuery(GET_CASH_DROP_DATA, {
        variables: {
            siteId: selected_site,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
        },
        fetchPolicy: 'no-cache', // Disable caching for this query
        skip: (
            selected_site === 'Select Site' ||
            (startDate.isAfter(endDate))
        )
    });

    React.useEffect(() => {
        if (cashDropGqlData !== undefined) {
            setCashDropData(cashDropGqlData.cashDropReport)
        }
    }, [cashDropGqlData])

    React.useEffect(() => {
        if (startDate.isAfter(endDate)) {
            setUiErrorMessage('The start-date cannot be after the end-date')
            setSnackBarOpen(true)
        }
    }, [startDate, endDate])

    React.useEffect(() => {
        setSelectedSite(selected_site)
    }, [selected_site])

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const handleChildChange = (event, newChartModeChild) => {
        if (newChartModeChild !== null) {
            setChartMode(newChartModeChild);
        }
    };

    return (
        <div>
            {
                selectedSite !== 'Select Site' ? (
                    <Grid container spacing={3} sx={{ marginTop:15 }}>
                        <Grid size={10} offset={{ sm: 2 }}>
                            <Card sx={{ boxShadow: 5, background: '#eaeaea' }}>
                                <CardContent sx={{textAlign: 'center'}}>
                                    <Box
                                        component="form"
                                        sx={{ '& > :not(style)': { m: 1, width: '25ch' } }}
                                        noValidate
                                        autoComplete="off"
                                    >
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Start Date"
                                                value={startDate}
                                                onChange={(newDate) => {
                                                    setStartDate(newDate);
                                                }}
                                                format="YYYY-MM-DD" // Custom date format
                                            />
                                            <DatePicker
                                                label="End Date"
                                                value={endDate}
                                                onChange={(newDate) => {
                                                    setEndDate(newDate);
                                                }}
                                                format="YYYY-MM-DD" // Custom date format
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={10} offset={{ sm: 2 }}>
                            <Card sx={{ boxShadow: 5, background: '#eaeaea' }}>
                                <CardContent sx={{textAlign: 'center'}}>
                                    <ToggleButtonGroup
                                        value={chartMode}
                                        exclusive
                                        onChange={handleChildChange}
                                        aria-label="Chart Mode Child"
                                    >
                                        <ToggleButtonStyled value="daily">Daily</ToggleButtonStyled>
                                        <ToggleButtonStyled value="weekly">Weekly</ToggleButtonStyled>
                                        <ToggleButtonStyled value="monthly">Monthly</ToggleButtonStyled>
                                    </ToggleButtonGroup>
                                    <br/>
                                    <br/>
                                    <CashDropChart
                                        view_mode={chartMode}
                                        chart_data={cashDropData}
                                        loading={cashDropDataLoading}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={10} offset={{ sm: 2 }}>
                            <Card sx={{ boxShadow: 5, background: '#eaeaea' }}>
                                <CardContent sx={{textAlign: 'center'}}>
                                    <CashDropTable
                                        loading={cashDropDataLoading}
                                        chart_data={cashDropData}
                                        start_date={startDate}
                                        end_date={endDate}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={8} sx={{ marginTop:15 }}>
                        <Grid size={10} offset={{ sm: 2 }}>
                            <Box sx={{ width: '100%', height: '600px' }}>
                                <Typography component="div" variant="h4" align="center">
                                    Loading Sites...
                                </Typography>
                                <LinearProgress
                                    sx={{
                                        height: 5, // Customize height
                                        marginTop: 5,
                                        backgroundColor: '#ff6600', // Background track color
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: 'black', // Bar color
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                )
            }
            <Snackbar
                open={snackBarOpen}
                autoHideDuration={4000}
                onClose={handleSnackBarClose}
                sx={{ width: '60%' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackBarClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {uiErrorMessage}
                </Alert>
            </Snackbar>
            <br/>
            <br/>
            <br/>
        </div>
    );
};

export default CashPage;